import { useCallback, useEffect, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import type { ChatSendBody, ChatTransport } from './ChatTransport';
import type { ChatMessage } from './types';

interface UseChatMessagesOptions {
  transport: ChatTransport;
  isOpen: boolean;
  currentSessionID: string | null;
  /**
   * The routine open in the Routine editor (URL-derived only, no
   * fallback). Forwarded into `ChatSendBody.editingRoutineID` so the
   * agent can resolve "this routine" against the page the operator
   * is on, not against whatever happens to be loaded.
   */
  editingRoutineID: string | null;
  /**
   * The routine loaded on the robot (`robot.latestRoutineID`).
   * Forwarded into `ChatSendBody.loadedRoutineID` so the agent can
   * resolve "the running routine" against the actual loaded id,
   * even when the user is editing a different one.
   */
  loadedRoutineID: string | null;
  /**
   * Optional dispatch hook for event shapes the standard handler
   * doesn't recognize (anything other than `text_delta`,
   * `assistant_message_persisted`, `user_message_persisted`,
   * `tool_use`, `tool_result`, `done`, and the legacy
   * `assistant_text`). Called with the raw event and an `append`
   * helper that pushes a message into the same state the hook owns.
   * Used by the dev harness (ui-mock) to layer mock-only event kinds
   * without subscribing to `/events` twice. Production builds use it
   * to dispatch `confirmation_request` into the panel provider.
   */
  onUnknownEvent?: (
    event: unknown,
    append: (message: ChatMessage) => void,
  ) => void;
  /**
   * [TFIX] Called when the BE allocates a sessionID for a
   * previously session-less /send (i.e. the first message in a
   * brand-new chat). The mount uses this to pin `currentSessionID`
   * in the panel provider so the next /send and the /events
   * subscription target the same session.
   */
  onSessionCreated?: (sessionID: string) => void;
}

/**
 * A turn-terminal failure surfaced by the orchestrator's `error` event
 * (codes: token_budget, llm_unavailable, max_iterations,
 * response_truncated, persistence_unavailable, session_deleted,
 * confirmation_timeout). Distinct from `streamError`, which is a
 * transport-level disconnect — and which the events handler clears on
 * every incoming frame, so it can't carry a terminal error.
 */
export interface AgentChatError {
  code: string;
  message: string;
}

interface UseChatMessagesResult {
  messages: ChatMessage[];
  isSending: boolean;
  streamError: Error | null;
  /**
   * Terminal agent error from the orchestrator, or null. Surfaced as a
   * banner; cleared on a new send, a session switch, or "New chat".
   */
  agentError: AgentChatError | null;
  /**
   * True while a turn is in flight but nothing else on screen is moving —
   * from send until the first streamed token or tool card, and again
   * between a tool result and the next LLM response. Those are the
   * longest silent stretches of a turn (the model is ingesting the
   * prompt or generating a large tool payload); the mount renders a
   * ThinkingIndicator so the agent doesn't look dead during them.
   * Inferred client-side from the event stream, so it can only say
   * "working", not what the work is.
   */
  isThinking: boolean;
  sendMessage: (text: string) => Promise<void>;
  /**
   * Re-post a previously-failed user message (marked
   * `sendStatus: 'failed'`). The matching bubble's status flips back
   * to undefined while the retry is in flight; on a fresh failure it
   * returns to 'failed' so the inline "Not sent. Tap to retry."
   * affordance reappears.
   */
  retryMessage: (messageID: string) => Promise<void>;
  /**
   * Drop messages and clear the stream-error banner. Call this when
   * the mount wants a clean slate (e.g. "New chat" from the kebab
   * menu) without waiting for a `currentSessionID` transition.
   */
  reset: () => void;
  /**
   * [TFIX] Exposed so the mount can seed message history from
   * feathers `agentMessages` after `useRoutineSessionBinding`
   * auto-binds a resumed session (or after the user picks a row from
   * the Chat history view). Only the mount should call this — the
   * events stream owns the rest of the lifecycle.
   */
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
}

/**
 * Owns the events subscription, message state, and send pipeline for
 * the Routine Agent Chat panel. Extracted from `RoutineAgentChatMount`
 * so the mount itself can focus on layout + slot wiring.
 *
 * Responsibilities:
 *
 * 1. Subscribe to `transport.events(...)` while the panel is open and
 *    a session id is bound. Reconnects, disconnects, and message
 *    reconciliation all live here.
 * 2. Reset local state when the session id transitions away from a
 *    previously-bound session (new session = clean slate; close +
 *    reopen on the same session keeps the history).
 * 3. POST `transport.send(...)` and route per-message failures back
 *    into the optimistic bubble's `sendStatus`.
 *
 * Dispatch handles the BE's streaming turn shape: `text_delta` chunks
 * accumulate into one assistant bubble keyed by a synthetic id;
 * `assistant_message_persisted` swaps in the real BE-issued id and
 * clears the streaming flag; `tool_use` / `tool_result` open and flip
 * rows on a controlled `ServerToolGroup`; `user_message_persisted`
 * reconciles the optimistic user bubble with the BE-issued id (or
 * appends a peer-client bubble when no optimistic one exists);
 * `done` defensively clears streaming + tool-group tracking. The
 * legacy `assistant_text` branch is retained for backward
 * compatibility with the ui-mock harness.
 */
export function useChatMessages({
  transport,
  isOpen,
  currentSessionID,
  editingRoutineID,
  loadedRoutineID,
  onUnknownEvent,
  onSessionCreated,
}: UseChatMessagesOptions): UseChatMessagesResult {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [streamError, setStreamError] = useState<Error | null>(null);
  const [agentError, setAgentError] = useState<AgentChatError | null>(null);
  const [isThinking, setIsThinking] = useState(false);
  // Tracks the most recent session whose state we reset. Used to
  // distinguish "session actually changed" (clean slate) from
  // "panel closed and reopened on the same session" (keep history).
  const lastResetSessionRef = useRef<string | null>(null);
  // Used by sendMessage's finally to skip setIsSending(false) after
  // the component unmounts (panel closed mid-flight, etc.).
  const isMountedRef = useRef(true);
  // Pin the transport for the lifetime of the consumer so swapping
  // the prop (which shouldn't happen in practice) doesn't tear down
  // the open /events subscription mid-conversation.
  const transportRef = useRef(transport);
  // Same idea for the extension callback: updates land in the ref
  // so the /events subscription doesn't tear down on every render.
  const onUnknownEventRef = useRef(onUnknownEvent);
  // [TFIX] Ref-pin the auto-create callback for the same reason —
  // sendMessage's identity already depends on `currentSessionID`,
  // we don't want it to also churn whenever the caller passes a
  // fresh `setCurrentSessionID` reference each render.
  const onSessionCreatedRef = useRef(onSessionCreated);
  // Mirror the messages array in a ref so `retryMessage` can look up
  // the failed message's text without re-creating itself on every
  // message change (which would churn the MessageList's `onRetry`
  // prop identity).
  const messagesRef = useRef<ChatMessage[]>(messages);
  // [TFIX] Tracks the synthetic id of the assistant message currently
  // accumulating text_delta chunks for the in-flight turn. Cleared
  // when assistant_message_persisted swaps in the BE-issued id, or
  // when `done` signals the turn is over.
  const streamingMessageIdRef = useRef<string | null>(null);
  // [TFIX] Tracks the synthetic id of the ServerToolGroup message
  // currently collecting tool_use rows. Cleared whenever a text_delta,
  // assistant_message_persisted, or done event signals the tool batch
  // is over — so the next tool_use opens a fresh group.
  const toolGroupMessageIdRef = useRef<string | null>(null);
  // [TFIX] Synthetic id of the optimistic user bubble inserted by the
  // last `sendMessage` call. The matching `user_message_persisted`
  // event swaps this id for the BE-issued messageID so the sender's
  // bubble lines up with what peer clients render (and with what the
  // seed loader reads back from feathers).
  const pendingUserMessageIdRef = useRef<string | null>(null);

  transportRef.current = transport;
  onUnknownEventRef.current = onUnknownEvent;
  onSessionCreatedRef.current = onSessionCreated;
  messagesRef.current = messages;

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Clear local state when the consumer drops `currentSessionID` to
  // null — typically the kebab menu's "New chat" action.
  useEffect(() => {
    if (currentSessionID !== null) return;

    lastResetSessionRef.current = null;
    streamingMessageIdRef.current = null;
    toolGroupMessageIdRef.current = null;
    pendingUserMessageIdRef.current = null;
    setMessages([]);
    setStreamError(null);
    setAgentError(null);
    setIsThinking(false);
  }, [currentSessionID]);

  // Subscribe to /events while the panel is open AND there's a
  // session to subscribe to. Without a sessionID there's nothing
  // session-scoped to listen for.
  useEffect(() => {
    if (!isOpen || currentSessionID === null) return undefined;

    const controller = new AbortController();

    // [TFIX] Genuine session SWITCH = clean slate, but only when
    // moving between two non-null session ids. The first-bind
    // (null → realID) case is NOT a switch: it's the BE allocating
    // an id for the just-sent message, and wiping at that point
    // would drop the user message we optimistically appended in
    // sendMessage and flash the panel back to the landing hero.
    // Likewise, a simple close → reopen on the same session must
    // keep the message history.
    if (
      lastResetSessionRef.current !== null &&
      lastResetSessionRef.current !== currentSessionID
    ) {
      streamingMessageIdRef.current = null;
      toolGroupMessageIdRef.current = null;
      pendingUserMessageIdRef.current = null;
      setMessages([]);
      setStreamError(null);
      setAgentError(null);
      setIsThinking(false);
    }

    lastResetSessionRef.current = currentSessionID;

    transportRef.current.events({
      sessionID: currentSessionID,
      onError: (error) => {
        if (controller.signal.aborted) return;

        setStreamError(error);

        // A disconnected stream can't vouch for turn progress — pulsing
        // dots under the "Stream disconnected" banner would assert
        // liveness the client can't see. Lowering is safe: the NDJSON
        // client reconnects with backoff and the events endpoint replays
        // missed frames, which re-raise or settle the indicator as the
        // state machine dictates.
        setIsThinking(false);
      },
      onEvent: (event) => {
        // Drop late events that arrive after the consumer aborted —
        // typically a session transition (currentSessionID changed)
        // or panel close. Without this guard, a buffered event could
        // clear the new subscription's error banner or inject a
        // message from the previous session.
        if (controller.signal.aborted) return;

        // Any event arriving on the stream proves the connection is
        // healthy again — clear the stale disconnect banner so it
        // doesn't sit alongside fresh messages after an
        // auto-reconnect.
        setStreamError(null);

        if (typeof event !== 'object' || event === null) {
          onUnknownEventRef.current?.(event, (message) => {
            setMessages((prev) => [...prev, message]);
          });

          return;
        }

        const eventType = (event as { type?: unknown }).type;

        // [TFIX] BE emits `user_message_persisted` (with `text`) once
        // the sender's prompt is committed to storage. Sender path:
        // swap the optimistic bubble's synthetic id for the BE-issued
        // messageID so it aligns with the seed loader and peer
        // clients. Peer path (no pending optimistic id): append a
        // fresh user bubble with the persisted id + text. Either
        // path: skip if a bubble with `messageID` is already present
        // (e.g. seed already loaded it).
        if (eventType === 'user_message_persisted') {
          const payload = event as { messageID?: unknown; text?: unknown };

          if (typeof payload.messageID !== 'string' || !payload.messageID) {
            return;
          }

          if (typeof payload.text !== 'string') {
            // Older BE without the text payload — nothing to render
            // peer-side. Sender already has the optimistic bubble.
            return;
          }

          const { messageID, text: msgText } = payload;
          const pendingID = pendingUserMessageIdRef.current;

          setMessages((prev) => {
            if (prev.some((m) => m.id === messageID)) return prev;

            if (pendingID !== null) {
              const idx = prev.findIndex((m) => m.id === pendingID);

              if (idx >= 0) {
                const next = prev.slice();

                next[idx] = { ...next[idx], id: messageID, messageID };

                return next;
              }
            }

            return [
              ...prev,
              { id: messageID, messageID, role: 'user', text: msgText },
            ];
          });

          pendingUserMessageIdRef.current = null;

          return;
        }

        // [TFIX] BE emits `text_delta` chunks for the streaming
        // assistant turn — accumulate them into one bubble keyed by a
        // synthetic id until `assistant_message_persisted` swaps in
        // the real BE-issued message id.
        if (eventType === 'text_delta') {
          const payload = event as { text?: unknown };

          if (typeof payload.text !== 'string' || payload.text.length === 0) {
            return;
          }

          // Text resumes after a tool batch — close the open tool
          // group so the next tool_use spawns a new card below this
          // text bubble.
          toolGroupMessageIdRef.current = null;

          // The streaming bubble (with its cursor) takes over as the
          // "agent is working" affordance.
          setIsThinking(false);

          const delta = payload.text;

          // Decide the target id synchronously so that subsequent
          // events fired in the same tick (`done`,
          // `assistant_message_persisted`) observe the ref via the
          // outer if-branches before React flushes the updater.
          // React may invoke set-state updaters more than once
          // (StrictMode, concurrent rendering), so we keep the ref
          // mutation out of the updater body.
          let targetID = streamingMessageIdRef.current;

          if (targetID === null) {
            targetID = `streaming-${Date.now()}-${Math.random()
              .toString(36)
              .slice(2, 8)}`;

            streamingMessageIdRef.current = targetID;
          }

          const resolvedID = targetID;

          setMessages((prev) => {
            const idx = prev.findIndex((m) => m.id === resolvedID);

            if (idx >= 0) {
              const next = prev.slice();

              next[idx] = {
                ...next[idx],
                text: next[idx].text + delta,
                isStreaming: true,
              };

              return next;
            }

            return [
              ...prev,
              {
                id: resolvedID,
                role: 'assistant',
                text: delta,
                isStreaming: true,
              },
            ];
          });

          return;
        }

        // [TFIX] BE emits `assistant_message_persisted` once the turn
        // is committed to storage. Swap the synthetic id for the real
        // one and clear the streaming flag so the bubble drops its
        // cursor and gains feedback affordances.
        if (eventType === 'assistant_message_persisted') {
          const payload = event as { messageID?: unknown };

          if (typeof payload.messageID !== 'string' || !payload.messageID) {
            return;
          }

          const { messageID } = payload;
          const streamingID = streamingMessageIdRef.current;

          setMessages((prev) => {
            if (streamingID !== null) {
              const idx = prev.findIndex((m) => m.id === streamingID);

              if (idx >= 0) {
                const next = prev.slice();

                next[idx] = {
                  ...next[idx],
                  id: messageID,
                  messageID,
                  isStreaming: false,
                };

                return next;
              }
            }

            return prev;
          });

          streamingMessageIdRef.current = null;
          toolGroupMessageIdRef.current = null;

          return;
        }

        // [TFIX] BE emits `done` when the turn is fully complete.
        // Defensive: clear streaming tracking in case
        // assistant_message_persisted didn't fire (e.g. a turn that
        // produced no assistant text).
        if (eventType === 'done') {
          if (streamingMessageIdRef.current !== null) {
            const streamingID = streamingMessageIdRef.current;

            setMessages((prev) => {
              const idx = prev.findIndex((m) => m.id === streamingID);

              if (idx >= 0) {
                const next = prev.slice();

                next[idx] = { ...next[idx], isStreaming: false };

                return next;
              }

              return prev;
            });

            streamingMessageIdRef.current = null;
          }

          toolGroupMessageIdRef.current = null;
          setIsThinking(false);

          return;
        }

        // [TFIX] BE emits one `tool_use` per tool invocation. Rows
        // accumulate into a single ServerToolGroupCard until a
        // text_delta / assistant_message_persisted / done event
        // closes the batch.
        if (eventType === 'tool_use') {
          const payload = event as {
            toolUseID?: unknown;
            toolName?: unknown;
          };

          if (
            typeof payload.toolUseID !== 'string' ||
            typeof payload.toolName !== 'string'
          ) {
            return;
          }

          const { toolUseID, toolName } = payload;

          // Close any open streaming text bubble so the tool card
          // sits below it instead of merging into the same row.
          streamingMessageIdRef.current = null;

          // The tool card's running row takes over as the "agent is
          // working" affordance. (The dots were shown during the silent
          // argument-generation phase via `tool_use_generating`.)
          setIsThinking(false);

          let groupID = toolGroupMessageIdRef.current;

          if (groupID === null) {
            groupID = `toolgroup-${Date.now()}-${Math.random()
              .toString(36)
              .slice(2, 8)}`;

            toolGroupMessageIdRef.current = groupID;
          }

          const resolvedGroupID = groupID;

          const newItem = {
            id: toolUseID,
            label: toolName,
            status: 'running' as const,
          };

          setMessages((prev) => {
            const idx = prev.findIndex((m) => m.id === resolvedGroupID);

            if (idx >= 0) {
              const target = prev[idx];

              if (target.card?.kind !== 'toolGroup') return prev;

              const nextTools = [...target.card.tools, newItem];
              const next = prev.slice();

              next[idx] = {
                ...target,
                card: { ...target.card, tools: nextTools },
              };

              return next;
            }

            return [
              ...prev,
              {
                id: resolvedGroupID,
                role: 'assistant',
                text: '',
                card: {
                  kind: 'toolGroup',
                  tools: [newItem],
                  controlled: true,
                },
              },
            ];
          });

          return;
        }

        // BE emits `tool_use_generating` when the model starts emitting a
        // tool call's JSON arguments — a silent stretch (no streamed text,
        // no card yet) that can run 20-30s for a large routine. Raise the
        // thinking dots so the agent doesn't look dead; the `tool_use` event
        // below lowers them when the arguments are complete and the card
        // appears.
        if (eventType === 'tool_use_generating') {
          setIsThinking(true);

          return;
        }

        // [TFIX] BE emits `tool_result` after each tool finishes.
        // Find the toolGroup card containing this toolUseID and flip
        // the matching row's status. Accept both camelCase
        // (`toolUseId`, lowercase 'd') and snake_case (`tool_use_id`)
        // payload keys for forward-compat with any future migration.
        if (eventType === 'tool_result') {
          const payload = event as {
            toolUseID?: unknown;
            toolUseId?: unknown;
            tool_use_id?: unknown;
            isError?: unknown;
            is_error?: unknown;
          };

          let toolUseID: string | null = null;

          if (typeof payload.toolUseID === 'string') {
            toolUseID = payload.toolUseID;
          } else if (typeof payload.toolUseId === 'string') {
            toolUseID = payload.toolUseId;
          } else if (typeof payload.tool_use_id === 'string') {
            toolUseID = payload.tool_use_id;
          }

          if (toolUseID === null) return;

          const useID = toolUseID;

          const errorFlag =
            payload.isError === true || payload.is_error === true;

          const nextStatus = errorFlag
            ? ('error' as const)
            : ('complete' as const);

          // A finished tool means the orchestrator is about to feed the
          // result back to the LLM — the next silent stretch. Re-raise
          // the thinking affordance until the next text_delta/tool_use.
          // (Tools in a batch dispatch sequentially, so if another tool
          // is still running its card row is also visible; the brief
          // overlap is acceptable.)
          setIsThinking(true);

          setMessages((prev) => {
            const idx = prev.findIndex(
              (m) =>
                m.card?.kind === 'toolGroup' &&
                m.card.tools.some((t) => t.id === useID),
            );

            if (idx < 0) return prev;

            const target = prev[idx];

            if (target.card?.kind !== 'toolGroup') return prev;

            const nextTools = target.card.tools.map((t) =>
              t.id === useID ? { ...t, status: nextStatus } : t,
            );

            const next = prev.slice();

            next[idx] = {
              ...target,
              card: { ...target.card, tools: nextTools },
            };

            return next;
          });

          return;
        }

        // Legacy `assistant_text` event — kept for backward
        // compatibility with older BE builds and the ui-mock harness.
        // No-op against the current production BE.
        if (eventType === 'assistant_text') {
          const payload = event as { id?: unknown; text?: unknown };

          if (
            typeof payload.id === 'string' &&
            typeof payload.text === 'string'
          ) {
            const { id } = payload;
            const { text } = payload;

            setMessages((prev) => {
              const idx = prev.findIndex((m) => m.id === id);

              if (idx >= 0) {
                const next = prev.slice();

                next[idx] = { ...next[idx], text };

                return next;
              }

              return [...prev, { id, role: 'assistant', text }];
            });

            setIsThinking(false);
          }

          return;
        }

        // BE emits `error` when a turn ends abnormally — the
        // orchestrator's terminal failure codes (token_budget,
        // llm_unavailable, max_iterations, …). The stream closes after
        // this, so surface the server-provided message as a banner the
        // operator can act on (token_budget asks them to start a new
        // chat). Note we do NOT clear it the way `streamError` is cleared
        // on every frame — no further frames arrive this turn, and the
        // banner must persist until the next send / new chat.
        if (eventType === 'error') {
          const payload = event as { code?: unknown; message?: unknown };

          const message =
            typeof payload.message === 'string' && payload.message.length > 0
              ? payload.message
              : 'The agent stopped unexpectedly.';

          const code =
            typeof payload.code === 'string' ? payload.code : 'unknown';

          // Mirror `done`: a turn that errored mid-stream may have left a
          // streaming bubble with a live cursor. Settle it so it doesn't
          // spin forever beneath the banner.
          const streamingID = streamingMessageIdRef.current;

          if (streamingID !== null) {
            setMessages((prev) => {
              const idx = prev.findIndex((m) => m.id === streamingID);

              if (idx < 0) return prev;

              const next = prev.slice();

              next[idx] = { ...next[idx], isStreaming: false };

              return next;
            });

            streamingMessageIdRef.current = null;
          }

          toolGroupMessageIdRef.current = null;

          setAgentError({ code, message });
          setIsThinking(false);

          return;
        }

        onUnknownEventRef.current?.(event, (message) => {
          setMessages((prev) => [...prev, message]);
        });
      },
      signal: controller.signal,
    });

    return () => {
      controller.abort();

      // With the subscription gone (panel closed, session switched, or
      // unmount), turn progress is unobservable — a turn that finishes
      // while closed would otherwise leave stale dots on reopen, since
      // its `done`/`error` frames were never seen. Lowering is safe for
      // turns still in flight: resubscribing drains the replay buffer,
      // which re-raises the indicator as the state machine dictates —
      // the same recovery path as a stream disconnect.
      setIsThinking(false);
    };
  }, [isOpen, currentSessionID]);

  // Post a `/send` for the given user-message id + text. On failure
  // flips that message's `sendStatus` to 'failed' so `UserMessage`
  // renders the red retry affordance; on success leaves it absent.
  //
  // [TFIX] When the send was issued with sessionID === null (first
  // message of a brand-new chat), parses the response body and pings
  // `onSessionCreated` with the BE-issued sessionID so the provider
  // can pin it for follow-up sends + the /events subscription.
  const postSend = useCallback(
    async (messageID: string, text: string) => {
      setIsSending(true);
      // Clear any prior terminal-error banner on a fresh send/retry. A
      // still-exhausted token budget will re-emit the error event and
      // re-raise the banner; a transient failure (e.g. llm_unavailable)
      // clears so the operator sees the retry proceed.
      setAgentError(null);
      // The turn is in flight from this moment until the first streamed
      // token / tool card; show the thinking affordance for the gap.
      setIsThinking(true);

      const body: ChatSendBody = {
        sessionID: currentSessionID,
        editingRoutineID,
        loadedRoutineID,
        text,
      };

      try {
        const response = await transportRef.current.send(body);

        if (!response.ok) {
          throw new Error(`POST /send returned HTTP ${response.status}`);
        }

        if (currentSessionID === null) {
          try {
            const data = (await response.clone().json()) as {
              sessionID?: unknown;
            };

            if (typeof data.sessionID === 'string' && data.sessionID) {
              onSessionCreatedRef.current?.(data.sessionID);
            }
          } catch {
            // Body wasn't JSON / didn't carry sessionID. Older BE
            // builds returned {turnID, startedAt} only; silently
            // skip so we don't surface a parse failure as a
            // send error.
          }
        }
      } catch {
        if (isMountedRef.current) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === messageID ? { ...m, sendStatus: 'failed' } : m,
            ),
          );

          // The send never started a turn — nothing is thinking. The
          // failed bubble's retry affordance is the active UI now.
          setIsThinking(false);
        }
      } finally {
        if (isMountedRef.current) setIsSending(false);
      }
    },
    [currentSessionID, editingRoutineID, loadedRoutineID],
  );

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();

      if (!trimmed || isSending) return;

      const userMessageID = `user-${Date.now()}`;

      // [TFIX] Pin the optimistic id so the matching
      // `user_message_persisted` event can swap it for the BE-issued
      // messageID (instead of appending a duplicate bubble).
      pendingUserMessageIdRef.current = userMessageID;

      setMessages((prev) => [
        ...prev,
        { id: userMessageID, role: 'user', text: trimmed },
      ]);

      await postSend(userMessageID, trimmed);
    },
    [postSend, isSending],
  );

  const retryMessage = useCallback(
    async (messageID: string) => {
      if (isSending) return;

      const target = messagesRef.current.find((m) => m.id === messageID);

      if (
        target === undefined ||
        target.role !== 'user' ||
        target.sendStatus !== 'failed'
      ) {
        return;
      }

      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageID ? { ...m, sendStatus: undefined } : m,
        ),
      );

      await postSend(messageID, target.text);
    },
    [postSend, isSending],
  );

  const reset = useCallback(() => {
    lastResetSessionRef.current = null;
    streamingMessageIdRef.current = null;
    toolGroupMessageIdRef.current = null;
    pendingUserMessageIdRef.current = null;
    setMessages([]);
    setStreamError(null);
    setAgentError(null);
    setIsThinking(false);
  }, []);

  return {
    messages,
    isSending,
    streamError,
    agentError,
    isThinking,
    sendMessage,
    retryMessage,
    reset,
    setMessages,
  };
}
